/**
 * PubNub Provider Component
 * 
 * Wraps the app with PubNub context for real-time messaging.
 * This provider initializes PubNub with the current user's credentials.
 */

import React, { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback, ReactNode } from "react";
import PubNub from "pubnub";
import { PubNubProvider as PubNubReactProvider } from "pubnub-react";
import { createPubNubInstance, CHANNEL_PREFIXES } from "./config";
import { pubnubTokenService } from "src/services";

// Module-level ref so logout (outside React) can call disconnectPubNub()
let activePubNubInstance: PubNub | null = null;

/**
 * Call from logout to immediately unsubscribe & go offline.
 * Safe to call multiple times or when no instance exists.
 */
export const disconnectPubNub = (): void => {
  if (activePubNubInstance) {
    activePubNubInstance.unsubscribeAll();
    activePubNubInstance = null;
  }
};

interface PubNubContextValue {
  pubnub: PubNub | null;
  isConnected: boolean;
  userId: string | null;
  error: string | null;
  isTokenReady: boolean;
}

const PubNubContext = createContext<PubNubContextValue>({
  pubnub: null,
  isConnected: false,
  userId: null,
  error: null,
  isTokenReady: false,
});

export const usePubNubContext = () => useContext(PubNubContext);

interface PubNubProviderProps {
  children: ReactNode;
  userId?: string | null;
  enabled?: boolean;
}

/**
 * PubNub Provider Component
 * 
 * @param children - Child components
 * @param userId - Current user's unique identifier
 * @param enabled - Whether to enable PubNub connection (default: true)
 */
export const PubNubAppProvider: React.FC<PubNubProviderProps> = ({
  children,
  userId = null,
  enabled = true,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTokenReady, setIsTokenReady] = useState(false);
  const tokenFetchedRef = useRef(false);
  const tokenRefreshInProgressRef = useRef(false);

  // Create PubNub instance synchronously (no token yet)
  const pubnub = useMemo(() => {
    if (!userId || !enabled) return null;
    
    try {
      const instance = createPubNubInstance(userId);
      return instance;
    } catch (err) {
      console.error("[PubNub] Failed to create instance:", err);
      setError("Failed to initialize PubNub");
      return null;
    }
  }, [userId, enabled]);

  // Fetch and apply PAM token asynchronously
  useEffect(() => {
    if (!pubnub) {
      setIsTokenReady(false);
      tokenFetchedRef.current = false;
      return;
    }

    let cancelled = false;

    const fetchToken = async () => {
      try {
        const token = await pubnubTokenService();
        if (cancelled) return;
        if (token) {
          pubnub.setToken(token);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("[PubNub] Failed to fetch auth token, continuing without it:", err);
      } finally {
        if (!cancelled) {
          tokenFetchedRef.current = true;
          setIsTokenReady(true);
        }
      }
    };

    fetchToken();

    return () => { cancelled = true; };
  }, [pubnub]);

  // Auto-refresh token (called on PNAccessDeniedCategory)
  const refreshToken = useCallback(async () => {
    if (!pubnub || tokenRefreshInProgressRef.current) return;
    tokenRefreshInProgressRef.current = true;
    try {
      const token = await pubnubTokenService();
      if (token) {
        pubnub.setToken(token);
      }
    } catch (err) {
      console.warn("[PubNub] Failed to refresh token:", err);
    } finally {
      tokenRefreshInProgressRef.current = false;
    }
  }, [pubnub]);

  // Handle connection status — only after token is ready
  useEffect(() => {
    if (!pubnub || !isTokenReady) {
      setIsConnected(false);
      return;
    }

    // Add listener for connection status
    const listener = {
      status: (statusEvent: PubNub.StatusEvent) => {
        if (statusEvent.category === "PNConnectedCategory") {
          setIsConnected(true);
          setError(null);
        } else if (statusEvent.category === "PNNetworkDownCategory") {
          setIsConnected(false);
          setError("Network connection lost");
          console.warn("[PubNub] Network down");
        } else if (statusEvent.category === "PNNetworkUpCategory") {
          setIsConnected(true);
          setError(null);
        } else if (statusEvent.category === "PNAccessDeniedCategory") {
          // Log forbidden channels but don't break the app — token may lack some channel grants
          const deniedChannels = (statusEvent as any)?.errorData?.payload?.channels || [];
          console.warn("[PubNub] Access denied for channels:", deniedChannels,
            "— update your PAM token to include these channels.");
          // Only show error if it's not just the global-presence channel
          if (!deniedChannels.every((ch: string) => ch === CHANNEL_PREFIXES.GLOBAL_PRESENCE)) {
            setError("Access denied on some channels. Check PAM token.");
          }
          // Auto-refresh token on access denied (token may have expired)
          refreshToken();
        }
      },
    };

    pubnub.addListener(listener);

    // Subscribe to global presence channel so user appears online while logged in
    // Note: If token doesn't grant access to global-presence, this will 403 silently
    pubnub.subscribe({
      channels: [CHANNEL_PREFIXES.GLOBAL_PRESENCE],
      withPresence: true,
    });

    // Store for external access (logout)
    activePubNubInstance = pubnub;

    // Cleanup on unmount
    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribeAll();
      activePubNubInstance = null;
    };
  }, [pubnub, isTokenReady, refreshToken]);

  const contextValue: PubNubContextValue = {
    pubnub,
    isConnected,
    userId,
    error,
    isTokenReady,
  };

  // If no PubNub instance, render nothing (no userId)
  if (!pubnub) {
    return null;
  }

  return (
    <PubNubContext.Provider value={contextValue}>
      <PubNubReactProvider client={pubnub}>
        {children}
      </PubNubReactProvider>
    </PubNubContext.Provider>
  );
};

export default PubNubAppProvider;
